import { AbsenceServiceModule, CalendarEvent, CalendarEventSurveyAnswer, CalendarServiceModule, FileServiceModule, LeaderboardServiceModule, ServiceModule, TextServiceModule, TimeTrackingServiceModule, UrlServiceModule } from "../types/serviceModuleTypes";
import { PaginationParameters, toQueryParameters } from "./Pagination";
import { doApiGet, doApiPost, doApiPatch, doApiDelete, CallResult } from "./Requests";

export const serviceModulesApi = {
    getAllServiceModules: async (): Promise<CallResult<ServiceModule[]>> => {
        return await doApiGet<ServiceModule[]>(`/api/companies/current/servicemodules`)
    },

    createServiceModule: async (params: {
        serviceModuleType: number;
        parentFolderServiceModuleId?: number;
    }): Promise<CallResult<ServiceModule>> => {
        return await doApiPost<typeof params, ServiceModule>(
            `/api/companies/current/servicemodules`,
            params
        );
    },

    updateServiceModuleOrder: async (orderedIds: number[]) => {
        return await doApiPatch(`/api/companies/current/servicemodules/order`, orderedIds)
    },

    updateServiceModule: async (serviceModuleId: number, data: Partial<ServiceModule>): Promise<CallResult<ServiceModule>> => {
        return await doApiPatch(`/api/companies/current/servicemodules/${serviceModuleId}`, data)
    },

    deleteServiceModule: async (serviceModuleId: number): Promise<CallResult<null>> => {
        return await doApiDelete(`/api/companies/current/servicemodules/${serviceModuleId}`)
    },

    // Calendar Module Functions
    getCalendarModule: async (serviceModuleId: number): Promise<CallResult<CalendarServiceModule>> => {
        return await doApiGet<CalendarServiceModule>(`/api/companies/current/servicemodules/calendars/${serviceModuleId}`);
    },

    updateCalendarModule: async (serviceModuleId: number, data: Partial<CalendarServiceModule>) => {
        return await doApiPatch(`/api/companies/current/servicemodules/calendars/${serviceModuleId}`, data);
    },

    getCalendarEvents: async (serviceModuleId: number, startDate: Date, endDate: Date): Promise<CallResult<CalendarEvent[]>> => {
        return await doApiGet<CalendarEvent[]>(`/api/companies/current/servicemodules/calendars/${serviceModuleId}/events?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`);
    },

    createCalendarEvent: async (serviceModuleId: number, eventData: any): Promise<CallResult<CalendarEvent>> => {
        return await doApiPost(`/api/companies/current/servicemodules/calendars/${serviceModuleId}/events`, eventData);
    },

    updateCalendarEvent: async (serviceModuleId: number, eventId: number, eventData: any): Promise<CallResult<CalendarEvent>> => {
        return await doApiPatch(`/api/companies/current/servicemodules/calendars/${serviceModuleId}/events/${eventId}`, eventData);
    },

    deleteCalendarEvent: async (serviceModuleId: number, eventId: number): Promise<CallResult<null>> => {
        return await doApiDelete(`/api/companies/current/servicemodules/calendars/${serviceModuleId}/events/${eventId}`)
    },

    getCalendarEventSurveyResponses: async (serviceModuleId: number, eventId: number): Promise<CallResult<CalendarEventSurveyAnswer[]>> => {
        return await doApiGet<CalendarEventSurveyAnswer[]>(`/api/companies/current/servicemodules/calendars/${serviceModuleId}/events/${eventId}/surveyResponses`)
    },

    // FolderServiceModule Functions
    // Folder Module
    getFolderChildren: async (serviceModuleId: number) => {
        return await doApiGet<ServiceModule[]>(`/api/companies/current/servicemodules/folders/${serviceModuleId}/children`);
    },
    updateFolderModule: async (serviceModuleId: number, data: Partial<ServiceModule>) => {
        return await doApiPatch<Partial<ServiceModule>, ServiceModule>(`/api/companies/current/servicemodules/folders/${serviceModuleId}`, data);
    },

    // File module
    getFileModule: async (serviceModuleId: number) => {
        return await doApiGet<FileServiceModule>(`/api/companies/current/servicemodules/files/${serviceModuleId}`);
    },
    updateFileModuleMetadata: async (serviceModuleId: number, data: Partial<ServiceModule>) => {
        return await doApiPatch<Partial<ServiceModule>, ServiceModule>(`/api/companies/current/servicemodules/files/${serviceModuleId}`, data);
    },

    uploadFile: async (serviceModuleId: number, data: { data: string; filename: string }) => {
        return await doApiPost<{ data: string; filename: string }, ServiceModule>(`/api/companies/current/servicemodules/files/${serviceModuleId}/file`, data);
    },

    deleteFile: async (serviceModuleId: number) => {
        return await doApiDelete<null>(`/api/companies/current/servicemodules/files/${serviceModuleId}/file`);
    },

    // Text module
    getTextModule: async (serviceModuleId: number) => {
        return await doApiGet<TextServiceModule>(`/api/companies/current/servicemodules/texts/${serviceModuleId}`);
    },

    updateTextModule: async (serviceModuleId: number, data: Partial<TextServiceModule>) => {
        return await doApiPatch<Partial<TextServiceModule>, TextServiceModule>(`/api/companies/current/servicemodules/texts/${serviceModuleId}`, data);
    },

    // URL module
    getUrlModule: async (serviceModuleId: number) => {
        return await doApiGet<UrlServiceModule>(`/api/companies/current/servicemodules/urls/${serviceModuleId}`);
    },

    updateUrlModule: async (serviceModuleId: number, data: Partial<UrlServiceModule>) => {
        return await doApiPatch<Partial<UrlServiceModule>, UrlServiceModule>(`/api/companies/current/servicemodules/urls/${serviceModuleId}`, data);
    },

    // Leaderboard module
    getLeaderboardModule: async (serviceModuleId: number) => {
        return await doApiGet<LeaderboardServiceModule>(`/api/companies/current/servicemodules/leaderboards/${serviceModuleId}`);
    },

    updateLeaderboardModule: async (serviceModuleId: number, data: Partial<LeaderboardServiceModule>) => {
        return await doApiPatch<Partial<LeaderboardServiceModule>, LeaderboardServiceModule>(`/api/companies/current/servicemodules/leaderboards/${serviceModuleId}`, data);
    },

    // Absence module
    getAbsenceModule: async (serviceModuleId: number): Promise<CallResult<AbsenceServiceModule>> => {
        return await doApiGet<AbsenceServiceModule>(
            `/api/companies/current/servicemodules/absence/${serviceModuleId}`
        );
    },

    updateAbsenceModule: async (serviceModuleId: number, data: Partial<AbsenceServiceModule>): Promise<CallResult<AbsenceServiceModule>> => {
        return await doApiPatch<Partial<AbsenceServiceModule>, AbsenceServiceModule>(
            `/api/companies/current/servicemodules/absence/${serviceModuleId}`,
            data
        );
    },

    // Timetracking module
    getTimeTrackingModule: async (serviceModuleId: number): Promise<CallResult<TimeTrackingServiceModule>> => {
        return await doApiGet<TimeTrackingServiceModule>(
            `/api/companies/current/servicemodules/timetracking/${serviceModuleId}`
        );
    },

    updateTimeTrackingModule: async (serviceModuleId: number, data: Partial<TimeTrackingServiceModule>): Promise<CallResult<TimeTrackingServiceModule>> => {
        return await doApiPatch<Partial<TimeTrackingServiceModule>, TimeTrackingServiceModule>(
            `/api/companies/current/servicemodules/timetracking/${serviceModuleId}`,
            data
        );
    },

    getLeaderboardActionModules: async (isCompanyWideAssigned: boolean, groupIds: number[]) => {
        const queryParams = new URLSearchParams();
        queryParams.append("isCompanyWideAssigned", isCompanyWideAssigned.toString());
        groupIds.forEach((id) => queryParams.append("groupIds", id.toString()));
        const url = `/api/companies/current/servicemodules/leaderboards/actionModules?${queryParams.toString()}`;
        return await doApiGet<any[]>(url);
    },

        getLeaderboardData: async ({
            serviceModuleId,
            actionModuleId,
            date,
            ...paginationParams
        }: PaginationParameters & {
            serviceModuleId: number;
            actionModuleId?: number;
            date?: string;
        }) => {
            const params = new URLSearchParams(toQueryParameters(paginationParams));

            if (actionModuleId) {
                params.append("actionModuleId", actionModuleId.toString());
            }

            if (date) {
                params.append("date", date);
            }

            return await doApiGet(
                `/api/companies/current/servicemodules/leaderboards/${serviceModuleId}/board?${params.toString()}`
            );
        },

            getLeaderboardDashboardData: async ({ serviceModuleId, actionModuleId }: {
                serviceModuleId: number;
                actionModuleId: number;
            }) => {
                return await doApiGet(`/api/companies/current/servicemodules/leaderboards/${serviceModuleId}/dashboard?actionModuleId=${actionModuleId}`);
            },
};
